import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import IFeedData, { newFeedData } from '@/models/comp/IFeedData';
import SmartExchangeRequest from '@/models/requests/SmartExchangeRequest';
import SmartExchangeSubsRequest from '@/models/requests/SmartExchangeSubsRequest';
import SmartExchangeService from '@/services/2.6/SmartExchangeService';
import TheWebEnvConfigService from '@/services/TheWebEnvConfigService';
import { Actions, Getters, Mutations, smartExchangeStore, store } from '@/store';
import CompDataFeedsRequest from '@/store/2.6/models/CompDataFeedsRequest';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import SmartExchangeClaimsManager from './SmartExchangeClaimsManager';

export default {
    /**
     * Fetch a feed data using the given smart exchange request.
     * @param request
     * @returns A feed data which may have no values when not able to retrieve
     */
    async fetchFeedData(request: SmartExchangeRequest): Promise<IFeedData> {
        const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
        const service = new SmartExchangeService(webEnvConfig);

        let feedData = await service.getBestComp(request);
        if (!feedData) {
            feedData = newFeedData();
        }

        return feedData;
    },

    async fetchFeedDataSubComps(feedRequest: CompDataFeedsRequest): Promise<void> {
        await commitSubComps(feedRequest);
    },

    /**
     * Fetch the active comp's sub comp data and save it to the store. Clear any previous sub comp data.
     */
    async fetchAndStoreSubComps(): Promise<void> {
        const activeComp = store.getters[Getters.CompAddresses.ActiveComp];
        await commitSubComps(activeComp);
    },
};

/**
 * Fetch subcomps using the given local comp request.
 * @param request
 * @returns An array of IComp which may be empty when none are retrieved
 */
async function fetchSubComps(request: SmartExchangeSubsRequest): Promise<IFeedData[]> {
    const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
    const service = new SmartExchangeService(webEnvConfig);

    let feedDatas = await service.getSubComps(request);
    if (!feedDatas) {
        feedDatas = [];
    }

    return feedDatas;
}

async function commitSubComps(feedRequest: CompDataFeedsRequest) {
    const comp = store.getters[Getters.CompAddresses.CompByAddress](feedRequest.address);

    const claims = await SmartExchangeClaimsManager.getClaims();

    const subsRequest: SmartExchangeSubsRequest = {
        formCode: theLocalStorageUtils.get(LocalStorageKeys.formCode) ?? '',
        address: comp.address,
        userAccessToken: claims.userAccessToken ?? '',
        isOptedIn: claims.isOptedIn ?? false,
    };

    const feedDatas = await fetchSubComps(subsRequest);

    store.commit(Mutations.CompAddresses.ClearSubComps, {
        comp: comp,
        initials: SourceDataInitials.SmartExchange,
    });

    if (feedDatas.length > 0) {
        for (const feedData of feedDatas) {
            store.commit(Mutations.CompAddresses.AddSubComp, {
                comp: comp,
                initials: SourceDataInitials.SmartExchange,
                sourceData: feedData.sourceData,
            });
        }
    }
}
